import React, { FC, useMemo } from 'react';
import { ExarePriceDetailsPriceElementType, RefundPricesFragment } from '@websky/graphql';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../theme';
import PriceItem from '../PriceItem/PriceItem';
import { Money as MoneyType } from '../../../../../types';
import TotalRefund from '../TotalRefund/TotalRefund';
import { getTotalPriceFilteredElements, getZeroPrice } from '@shared/lib/money';
import { DetailItem } from './types';

interface Props {
	prices: RefundPricesFragment;
	ticketPrice?: MoneyType;
	className?: string;
	items?: DetailItem[];
}

const ShortDetails: FC<Props> = ({ prices, ticketPrice, className, items }) => {
	const { t } = useTranslation('Refund');
	const { ShortDetails: css } = useTheme('PriceDetails');

	const details = useMemo(() => {
		if (items) {
			return items;
		}

		const detailsItems: DetailItem[] = [];

		const priceDetails = prices.priceDetails;
		const currentCurrency = prices.basePrice?.currency;

		detailsItems.push({
			name: `${t('Ticket price')}:`,
			price: prices.basePrice || getZeroPrice(currentCurrency)
		});

		detailsItems.push({
			name: t('Cost of refundable services'),
			price: getTotalPriceFilteredElements(
				priceDetails?.refunded.elements,
				ExarePriceDetailsPriceElementType.gdsServiceFare,
				currentCurrency
			)
		});

		detailsItems.push({
			name: t('Cost of non-refundable services'),
			price: getTotalPriceFilteredElements(
				priceDetails?.retention.elements,
				ExarePriceDetailsPriceElementType.gdsServiceFare,
				currentCurrency
			),
			isMinus: true
		});

		detailsItems.push({
			name: t('Fare refund penalty'),
			price: getTotalPriceFilteredElements(
				priceDetails?.retention.elements,
				ExarePriceDetailsPriceElementType.taxesRetention,
				currentCurrency
			),
			isMinus: true
		});

		return detailsItems;
	}, [t, prices, ticketPrice, items]);

	return (
		<div className={cn(css.root, className)}>
			<div className={css.details}>
				{details.length > 0 &&
					details.map((detail, index) => (
						<PriceItem
							key={index}
							className={css.item}
							name={detail.name}
							price={detail.price}
							isMinus={detail.isMinus}
						/>
					))}
			</div>

			<TotalRefund className={css.totalRefund} price={prices.priceToPay} />
		</div>
	);
};

export default ShortDetails;
